/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {ResponseData} from '@dvrd/dvr-controls';
import EmailStep from '../../../models/emailStepModel';
import {ActivateHandle, CustomerType, EmailFlow} from '../../../util/interfaces';
import {createErrorMessage} from '../../../util/utils';
import FlowSelect from './flowSelect';
import {AppContext} from '../../misc/appContext';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onReplaceStep: (step: EmailStep) => void;
}

function FlowSelectController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const {onReplaceStep} = props;
    const [selectedFlow, setSelectedFlow] = useState<EmailFlow | null>(null);
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setSelectedFlow(null);
    }

    function onSelectFlow(flow: EmailFlow) {
        return function () {
            setSelectedFlow(flow);
        };
    }

    function onSubmit() {
        if (!account || !selectedFlow) return;
        setLoading(true);
        const withInvoice = [EmailFlow.REVIEW_INVOICE, EmailFlow.INVOICE].includes(selectedFlow);
        const days = selectedFlow === EmailFlow.REVIEW_INVOICE ? 7 : 0;
        const step = new EmailStep({
            active: false,
            label: labelFromFlow(selectedFlow),
            days,
            flow: selectedFlow,
            with_invoice: withInvoice,
            account_id: account.id,
            customer_type: CustomerType.BOTH,
            product_selection_json: {clauses: [{clause_type: 'include', selection_type: 'all'}]}
        });
        step.save((step: EmailStep, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onReplaceStep(step);
                onClose();
            } else
                showDialog(
                    createErrorMessage(
                        data.message ?? t('email.flow.createFailed', 'Het instellen van de e-mail is niet gelukt.')
                    ),
                    t('email.flow.createFailedTitle', 'Instellen mislukt')
                );
        });
    }

    function labelFromFlow(flow: EmailFlow): string {
        switch (flow) {
            case EmailFlow.REVIEW_INVOICE:
                return t('email.flow.label.reviewInvoice', 'Review + factuur mail');
            case EmailFlow.CREDIT_INVOICE:
                return t('email.flow.label.creditInvoice', 'Creditfactuur mail');
            case EmailFlow.INVOICE:
                return t('email.flow.label.invoice', 'Factuur mail');
            case EmailFlow.OTHER:
                return t('email.flow.label.other', 'Overige mail');
            case EmailFlow.PRODUCT:
                return t('email.flow.label.product', 'Productspecifieke mail');
            case EmailFlow.REVIEW:
                return t('email.flow.label.review', 'Review mail');
        }
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <FlowSelect
            onSelectFlow={onSelectFlow}
            onSubmit={onSubmit}
            onClose={onClose}
            loading={loading}
            selectedFlow={selectedFlow}
            active={active}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(FlowSelectController);
